import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Calendar, MapPin, Edit, Trash2, BarChart3, DollarSign, Radio, QrCode, X, Plus, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents } from '@/lib/mockData';
import { formatDate, formatCurrency, formatNumber } from '@/lib/utils';
import { toast } from 'sonner';

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button 
      onClick={handleCopy}
      className="p-2 hover:bg-secondary rounded-lg transition-colors flex-shrink-0"
    >
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
    </button>
  );
};

// Edit Event Sheet Component - Same as in AdminEventList
const EditEventSheet = ({ event, isOpen, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    title: event?.title || '',
    date: event?.date?.split('T')[0] || '',
    livestreamEnabled: event?.livestreamEnabled || false,
    generalPrice: event?.ticketTypes?.[0]?.price || 50,
    vipPrice: event?.ticketTypes?.[1]?.price || 150,
    livestreamPrice: event?.ticketTypes?.[2]?.price || 25,
    streamName: event?.streamConfig?.streamName || '',
    latencyMode: event?.streamConfig?.latencyMode || 'low',
    dvrWindow: event?.streamConfig?.dvrWindow || 30,
    playbackPolicy: event?.streamConfig?.playbackPolicy || 'public',
    autoRecord: event?.streamConfig?.autoRecord || false,
    ingestUrl: event?.streamConfig?.ingestUrl || '',
    streamKey: event?.streamConfig?.streamKey || '',
    playbackUrl: event?.streamConfig?.playbackUrl || '',
    streamStatus: event?.streamConfig?.streamStatus || 'inactive'
  });

  if (!isOpen || !event) return null;

  const handleSave = () => {
    onSave(formData);
    toast.success('Event updated successfully');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="edit-event-sheet">
      <div className="absolute inset-0 bg-black/60 hidden lg:block" onClick={onClose} />
      
      <div className="relative w-full lg:max-w-lg bg-card lg:border-l border-border h-full overflow-hidden flex flex-col animate-in slide-in-from-right">
        <div className="bg-card border-b border-border p-4 flex items-center gap-3 flex-shrink-0">
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
          <h2 className="text-lg font-semibold text-white">Edit Event</h2>
        </div>

        <div className="flex border-b border-border flex-shrink-0">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'details' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Event Details
          </button>
          <button
            onClick={() => setActiveTab('streaming')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'streaming' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Streaming Config
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 lg:p-5 space-y-5">
          {activeTab === 'details' ? (
            <>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Event Name</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-secondary border-border"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Event Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-secondary border-border"
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="text-sm text-white">Enable Livestream</label>
                <Switch
                  checked={formData.livestreamEnabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, livestreamEnabled: checked })}
                />
              </div>

              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-sm font-medium text-white">Ticket Prices</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">IRL General</label>
                    <Input
                      type="number"
                      value={formData.generalPrice}
                      onChange={(e) => setFormData({ ...formData, generalPrice: e.target.value })}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">IRL VIP</label>
                    <Input
                      type="number"
                      value={formData.vipPrice}
                      onChange={(e) => setFormData({ ...formData, vipPrice: e.target.value })}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Livestream</label>
                    <Input
                      type="number"
                      value={formData.livestreamPrice}
                      onChange={(e) => setFormData({ ...formData, livestreamPrice: e.target.value })}
                      className="bg-secondary border-border"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <h3 className="text-sm font-medium text-white">Event Images</h3>
                <div className="flex gap-2 flex-wrap">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden">
                    <img src={event.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden">
                    <img src={event.coverImage} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Stream Name</label>
                <Input
                  value={formData.streamName}
                  onChange={(e) => setFormData({ ...formData, streamName: e.target.value })}
                  className="bg-secondary border-border"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Latency Mode</label>
                <select
                  value={formData.latencyMode}
                  onChange={(e) => setFormData({ ...formData, latencyMode: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-white"
                >
                  <option value="low">Low Latency</option>
                  <option value="normal">Normal</option>
                  <option value="high">High Quality</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">DVR Window (minutes)</label>
                <Input
                  type="number"
                  value={formData.dvrWindow}
                  onChange={(e) => setFormData({ ...formData, dvrWindow: e.target.value })}
                  className="bg-secondary border-border"
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="text-sm text-white">Auto Record</label>
                <Switch
                  checked={formData.autoRecord}
                  onCheckedChange={(checked) => setFormData({ ...formData, autoRecord: checked })}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Ingest URL</label>
                <div className="flex items-center gap-2">
                  <Input value={formData.ingestUrl} readOnly className="bg-secondary border-border flex-1 font-mono text-xs" />
                  <CopyButton text={formData.ingestUrl} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Stream Key</label>
                <div className="flex items-center gap-2">
                  <Input value={formData.streamKey} readOnly className="bg-secondary border-border flex-1 font-mono text-xs" />
                  <CopyButton text={formData.streamKey} />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bg-card border-t border-border p-4 flex-shrink-0">
          <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11">
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export function AdminEditEventInfo() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];

  const handleEditSave = (formData) => {
    // In production, this would update the event in the database
    console.log('Saving event:', formData);
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <Link to="/admin" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Events
        </Link>

        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{event.title}</h1>
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{event.venue}, {event.city}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            {/* Edit button opens the same EditEventSheet as the list edit icon */}
            <Button 
              variant="outline" 
              className="border-border"
              onClick={() => setIsEditSheetOpen(true)}
              data-testid="edit-event-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Link to={`/admin/events/${eventId}/delete`}>
              <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive hover:text-white">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </Link>
          </div>
        </div>

        {/* Event Image */}
        <Card className="bg-card border-border overflow-hidden mb-8">
          <img 
            src={event.coverImage || event.image} 
            alt={event.title}
            className="w-full h-64 object-cover"
          />
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(event.totalRevenue)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tickets Sold</p>
                <p className="text-2xl font-bold text-white">{formatNumber(event.ticketsSold)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Radio className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Page Views</p>
                <p className="text-2xl font-bold text-white">{formatNumber(event.views)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <QrCode className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Check-ins</p>
                <p className="text-2xl font-bold text-white">{formatNumber(Math.floor(event.ticketsSold * 0.85))}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to={`/admin/events/${eventId}/revenue`}>
            <Card className="bg-card border-border p-4 hover:border-primary/50 transition-colors cursor-pointer">
              <DollarSign className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-white">Revenue Details</h3>
              <p className="text-sm text-muted-foreground">View detailed revenue breakdown</p>
            </Card>
          </Link>
          
          <Link to={`/admin/events/${eventId}/stream/monitor`}>
            <Card className="bg-card border-border p-4 hover:border-primary/50 transition-colors cursor-pointer">
              <Radio className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-white">Stream Monitor</h3>
              <p className="text-sm text-muted-foreground">Monitor livestream status</p>
            </Card>
          </Link>
          
          <Link to={`/admin/events/${eventId}/checkin`}>
            <Card className="bg-card border-border p-4 hover:border-primary/50 transition-colors cursor-pointer">
              <QrCode className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-white">Check-in Scanner</h3>
              <p className="text-sm text-muted-foreground">Scan and verify tickets</p>
            </Card>
          </Link>
          
          <Link to={`/admin/events/${eventId}/analytics`}>
            <Card className="bg-card border-border p-4 hover:border-primary/50 transition-colors cursor-pointer">
              <BarChart3 className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-white">Analytics</h3>
              <p className="text-sm text-muted-foreground">View event performance</p>
            </Card>
          </Link>
        </div>
      </div>

      {/* Edit Event Sheet - Same as in AdminEventList */}
      <EditEventSheet
        event={event}
        isOpen={isEditSheetOpen}
        onClose={() => setIsEditSheetOpen(false)}
        onSave={handleEditSave}
      />
    </AdminLayout>
  );
}

export default AdminEditEventInfo;
